/**
 * The two questions every surface has to ask about a compaction row.
 *
 * They live here rather than in `MessageList` because the TRANSCRIPT and the
 * EXPORT must answer them identically, and they already disagreed: the export
 * accepted only the message-level `synthetic` flag, so every auto-continue
 * nudge written before that flag existed still exported under "## User" with
 * words the user never said — the exact defect the export was fixed for, on the
 * surface that outlives the session. Two copies of a predicate is how that
 * happens; this is the one implementation.
 *
 * Deliberately dependency-free (a structural type, not `MessageItem`) so any
 * caller with `{ role, parts }` can use it without importing a component.
 */

/** The subset of a UI message these predicates read. */
export interface CompactionRow {
  role: string
  parts: Array<{ type: string; synthetic?: boolean }>
  modelID?: string
  /** `info.summary` — the flag `process()` actually sets. */
  isSummary?: boolean
  /** Message-level "the system wrote this user turn, not the person". */
  synthetic?: boolean
}

/**
 * An assistant message that IS a compaction summary.
 *
 * `isSummary` is the authority. The `modelID` test is a LEGACY fallback and is
 * not equivalent: `process()` writes `modelID: input.model ?? 'compaction'`, so
 * it only reads 'compaction' while every caller leaves `model` unset — the day
 * one passes a model, a modelID-only check stops matching and the raw summary
 * markdown renders as an ordinary assistant bubble.
 */
export function isCompactionSummary(msg: CompactionRow): boolean {
  return msg.role === 'assistant' && (msg.isSummary === true || msg.modelID === 'compaction')
}

/**
 * A user turn compaction WROTE, rather than the person typing it — the
 * auto-continue nudge and the overflow replay.
 *
 * Message-level `synthetic` is the current shape; the all-parts test covers
 * rows written before it, where only the text part carried the flag. Requires
 * at least one text part so an empty message cannot match vacuously.
 */
export function isSyntheticUserTurn(msg: CompactionRow): boolean {
  if (msg.synthetic === true) return true
  const texts = msg.parts.filter((p) => p.type === 'text')
  return texts.length > 0 && texts.every((p) => p.synthetic === true)
}

/*
 * REMOVED: `isNudgeArtifactReply`.
 *
 * It hid the assistant's reply to compaction's auto-continue nudge — the
 * "No next steps needed." that used to end every compaction. Two versions
 * shipped and both were unsafe, for the same underlying reason.
 *
 * The first keyed on "this reply did no work" (no tool-call / patch / file
 * part). But a prose answer with no tool call is the ORDINARY shape of a final
 * assistant response, so a long turn that overflowed and then wrote its
 * conclusion as text had that conclusion deleted — from the transcript AND
 * from the export.
 *
 * The second tried to narrow it to the nudge via `compaction_continue`
 * metadata, on the theory that the OTHER synthetic turn — the overflow replay —
 * was the one whose answer is real. That theory is false in this codebase:
 * `compactNow`'s replay branch is gated on `input.overflow`, which neither
 * caller passes (the worker sends `auto: true`, the /summarize route
 * `auto: false`). So every auto-compaction writes the nudge, the discriminator
 * matches every time, and the unsafe "did no work" test was doing all the work
 * again.
 *
 * There is no structural signal separating "I have nothing to add" from "here
 * is the answer, in words". Losing a user's answer is far worse than showing an
 * odd line, so nothing is hidden. The real fix is upstream — stop the nudge
 * producing a user-facing non-answer at all — and belongs with the compaction
 * mechanism, not a transcript predicate.
 */
